import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { 
  Search, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  Target,
  Users,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

interface RequirementSubmission {
  requirement: string;
  submissions: number;
  avgScore: number;
  client: string;
  status: 'active' | 'inactive' | 'closed';
  createdDate: string;
  lastSubmission: string;
}

interface RequirementSubmissionsTableProps {
  data: RequirementSubmission[];
  isLoading?: boolean;
}

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const RequirementSubmissionsTable: React.FC<RequirementSubmissionsTableProps> = ({
  data,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof RequirementSubmission>('submissions');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(item =>
      item.requirement.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.client.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });

    return filtered;
  }, [data, searchTerm, sortField, sortDirection]);

  // Reset to page 1 when search changes
  useMemo(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredAndSortedData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSort = (field: keyof RequirementSubmission) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: keyof RequirementSubmission) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-text-secondary" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-primary-500" />
      : <ArrowDown className="w-4 h-4 text-primary-500" />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-success-500/20 text-success-500 border-success-500/30';
      case 'inactive':
        return 'bg-warning-500/20 text-warning-500 border-warning-500/30';
      case 'closed':
        return 'bg-error-500/20 text-error-500 border-error-500/30';
      default:
        return 'bg-text-secondary/20 text-text-secondary border-text-secondary/30';
    }
  };

  if (isLoading) {
    return (
      <Card className="glass-card">
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-glass-surface dark:bg-glass-surface-dark-mode rounded mb-2"></div>
            <div className="h-4 bg-glass-surface dark:bg-glass-surface-dark-mode rounded w-2/3"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="h-4 bg-glass-surface dark:bg-glass-surface-dark-mode rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Target className="w-5 h-5 text-accent-500" />
          <span>Requirement-wise Uploads</span>
        </CardTitle>
        <CardDescription>
          Detailed breakdown of uploads per requirement with search and sort capabilities
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Search and Controls */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <Input
              placeholder="Search requirements or clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-glass-surface dark:bg-glass-surface-dark-mode border-glass-border dark:border-glass-border-dark-mode"
            />
          </div>
          
          {/* Pagination Controls */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-secondary whitespace-nowrap">Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="px-2 py-1.5 text-sm rounded-lg border border-glass-border dark:border-glass-border-dark-mode bg-glass-surface dark:bg-glass-surface-dark-mode text-text-enterprise-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {ITEMS_PER_PAGE_OPTIONS.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-1.5 h-8 w-8"
              title="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1.5 h-8 w-8"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <span className="px-3 py-1 text-sm text-text-enterprise-primary whitespace-nowrap">
              Page {currentPage} of {totalPages || 1}
            </span>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-1.5 h-8 w-8"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage >= totalPages}
              className="p-1.5 h-8 w-8"
              title="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="text-sm text-text-secondary whitespace-nowrap">
            {filteredAndSortedData.length > 0 ? (
              <>Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length}</>
            ) : (
              <>0 of {data.length} requirements</>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-glass-border dark:border-glass-border-dark-mode">
                <th className="text-left py-3 px-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('requirement')}
                    className="flex items-center space-x-2 hover:bg-glass-surface dark:hover:bg-glass-surface-dark-mode text-base font-semibold"
                  >
                    <span>Requirement</span>
                    {getSortIcon('requirement')}
                  </Button>
                </th>
                <th className="text-left py-3 px-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('client')}
                    className="flex items-center space-x-2 hover:bg-glass-surface dark:hover:bg-glass-surface-dark-mode text-base font-semibold"
                  >
                    <span>Client</span>
                    {getSortIcon('client')}
                  </Button>
                </th>
                <th className="text-left py-3 px-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('submissions')}
                    className="flex items-center space-x-2 hover:bg-glass-surface dark:hover:bg-glass-surface-dark-mode text-base font-semibold"
                  >
                    <Users className="w-4 h-4" />
                    <span>Uploads</span>
                    {getSortIcon('submissions')}
                  </Button>
                </th>
                <th className="text-left py-3 px-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('avgScore')}
                    className="flex items-center space-x-2 hover:bg-glass-surface dark:hover:bg-glass-surface-dark-mode text-base font-semibold"
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>Avg Score</span>
                    {getSortIcon('avgScore')}
                  </Button>
                </th>
                <th className="text-left py-3 px-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('status')}
                    className="flex items-center space-x-2 hover:bg-glass-surface dark:hover:bg-glass-surface-dark-mode text-base font-semibold"
                  >
                    <span>Status</span>
                    {getSortIcon('status')}
                  </Button>
                </th>
                <th className="text-left py-3 px-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('lastSubmission')}
                    className="flex items-center space-x-2 hover:bg-glass-surface dark:hover:bg-glass-surface-dark-mode text-base font-semibold"
                  >
                    <span>Last Submission</span>
                    {getSortIcon('lastSubmission')}
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((requirement, index) => (
                <tr 
                  key={startIndex + index} 
                  className="border-b border-glass-border dark:border-glass-border-dark-mode hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 transition-colors"
                >
                  <td className="py-4 px-4">
                    <div>
                      <p className="text-base font-medium text-text-enterprise-primary">
                        {requirement.requirement}
                      </p>
                      <p className="text-sm text-text-enterprise-secondary">
                        Created: {requirement.createdDate}
                      </p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-base text-text-enterprise-primary">
                      {requirement.client}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-20 bg-glass-surface dark:bg-glass-surface-dark-mode rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full"
                          style={{ width: `${Math.min((requirement.submissions / 50) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-base font-bold text-primary-500 min-w-[2rem]">
                        {requirement.submissions}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <span className={`text-base font-bold ${
                        requirement.avgScore >= 80 ? 'text-success-500' :
                        requirement.avgScore >= 70 ? 'text-warning-500' :
                        'text-error-500'
                      }`}>
                        {requirement.avgScore}%
                      </span>
                      <div className="w-16 bg-glass-surface dark:bg-glass-surface-dark-mode rounded-full h-1">
                        <div 
                          className={`h-1 rounded-full ${
                            requirement.avgScore >= 80 ? 'bg-success-500' :
                            requirement.avgScore >= 70 ? 'bg-warning-500' :
                            'bg-error-500'
                          }`}
                          style={{ width: `${requirement.avgScore}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium border ${getStatusColor(requirement.status)}`}>
                      {requirement.status.charAt(0).toUpperCase() + requirement.status.slice(1)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-base text-text-enterprise-secondary">
                      {requirement.lastSubmission}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginatedData.length === 0 && (
          <div className="text-center py-8">
            <Target className="w-12 h-12 text-text-secondary mx-auto mb-4" />
            <p className="text-body-lg text-text-secondary">No requirements found</p>
            <p className="text-caption text-text-secondary">
              Try adjusting your search criteria
            </p>
          </div>
        )}

        {/* Bottom Pagination */}
        {filteredAndSortedData.length > itemsPerPage && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-glass-border dark:border-glass-border-dark-mode">
            <div className="text-sm text-text-secondary">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length} requirements
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-1.5 h-8 w-8"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1.5 h-8 w-8"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-primary-500 text-white' : ''}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="p-1.5 h-8 w-8"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage >= totalPages}
                className="p-1.5 h-8 w-8"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RequirementSubmissionsTable;
